import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useSuppliers } from '../../graphql/queries';
import { updateSupplier, deleteSupplier, ApiError } from '../../lib/apiClient';
import { LoadingSpinner, EmptyState, RichTextEditor, isDeltaEmpty } from '../../components/shared';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';

export const Route = createFileRoute('/suppliers/$supplierId/edit')({
  component: EditSupplierPage,
});

export { EditSupplierPage };

function EditSupplierPage(): JSX.Element {
  const { supplierId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const { data, loading, error: fetchError } = useSuppliers();
  const supplier = data?.suppliers?.find((s) => s.entityId === supplierId);

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (supplier) {
      setName(supplier.name);
      setDescription(supplier.description ?? '');
      setWebsiteUrl(supplier.websiteUrl ?? '');
    }
  }, [supplier]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await updateSupplier(supplierId, {
        name: name.trim(),
        description: isDeltaEmpty(description) ? undefined : description,
        websiteUrl: websiteUrl.trim() || undefined,
      });
      await apolloClient.refetchQueries({ include: ['ListSuppliers'] });
      void navigate({ to: '/suppliers' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update supplier');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!window.confirm(`Delete "${supplier?.name}"? This cannot be undone.`)) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await deleteSupplier(supplierId);
      await apolloClient.refetchQueries({ include: ['ListSuppliers'] });
      void navigate({ to: '/suppliers' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete supplier');
      setIsSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner centered message="Loading supplier..." />;

  if (fetchError || !supplier) {
    return (
      <EmptyState
        icon="error"
        title="Supplier not found"
        description={fetchError?.message ?? 'Supplier not found'}
        actionLabel="Back to Suppliers"
        onAction={() => void navigate({ to: '/suppliers' })}
      />
    );
  }

  return (
    <div className="simple-entity-form-page">
      <div className="simple-entity-form-page__back-link">
        <Link to="/suppliers" className="simple-entity-form-page__back-link-inner">
          <IconElement
            name="arrow_back"
            aria-hidden="true"
            className="simple-entity-form-page__back-icon"
          />
          <span className="forge-typography--body2">Back to Suppliers</span>
        </Link>
      </div>

      <h1 className="forge-typography--headline4 simple-entity-form-page__title">Edit Supplier</h1>

      {error && (
        <p role="alert" className="simple-entity-form-page__error">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="simple-entity-form-page__form">
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Name <span className="simple-entity-form-page__required">*</span>
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Website URL
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Description
          </label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Notes about this supplier, contract details, etc."
          />
        </div>
        <div className="simple-entity-form-page__actions simple-entity-form-page__actions--split">
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={() => void handleDelete()}
            disabled={isSubmitting}
            className="simple-entity-form-page__delete-button"
          >
            Delete Supplier
          </ForgeButton>
          <div className="simple-entity-form-page__actions-group">
            <Link to="/suppliers">
              <ForgeButton type="button" variant="outlined" disabled={isSubmitting}>
                Cancel
              </ForgeButton>
            </Link>
            <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
              Save Changes
            </ForgeButton>
          </div>
        </div>
      </form>
    </div>
  );
}
